import { useEffect, useState } from "react";
import ReactSelect from "react-select";
import { listMedicos, createMedico, updateMedico } from "../api/odontologia";
import { listUsuarios } from "../api/usuarios";
import Loader from "../components/Loader";
import { FiUser, FiUserPlus, FiEdit } from "react-icons/fi";

const MedicosOdontologia = () => {
  const [loading, setLoading]       = useState(true);
  const [medicos, setMedicos]       = useState([]);
  const [usuarios, setUsuarios]     = useState([]);
  const [showModal, setShowModal]   = useState(false);
  const [editMode, setEditMode]     = useState(false);
  const [currentId, setCurrentId]   = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ id_usuario: "", status: 1 });

  // ─── Carga de datos ─────────────────────────────────────────────
  const fetchMedicos = async () => {
    setLoading(true);
    try {
      const res = await listMedicos();
      setMedicos(res?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMedicos(); }, []);

  useEffect(() => {
    if (showModal && usuarios.length === 0) {
      listUsuarios()
        .then(res => setUsuarios(Array.isArray(res) ? res : (res?.data || [])))
        .catch(console.error);
    }
  }, [showModal]);

  // ─── Opciones Select ────────────────────────────────────────────
  // IDs de usuarios que ya son médicos
  const idsYaRegistrados = new Set(medicos.map(m => m.id_usuario?.toString()));

  // En crear: excluir los que ya tienen registro; en editar: mostrar todos (queda deshabilitado de todas formas)
  const opcionesUsuarios = usuarios
    .filter(u => editMode || !idsYaRegistrados.has(u.id.toString()))
    .map(u => ({
      value: u.id.toString(),
      label: `${u.nombre} ${u.apellido} — @${u.username}`,
      correo: u.correo,
    }));

  const opcionesEstado = [
    { value: 1, label: "Activo"   },
    { value: 0, label: "Inactivo" },
  ];

  // ─── Handlers ───────────────────────────────────────────────────
  const openCreate = () => {
    setForm({ id_usuario: "", status: 1 });
    setEditMode(false);
    setCurrentId(null);
    setShowModal(true);
  };

  const openEdit = (m) => {
    setForm({ id_usuario: m.id_usuario?.toString(), status: m.status });
    setEditMode(true);
    setCurrentId(m.id);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id_usuario) return;
    setSubmitting(true);
    try {
      const payload = { id_usuario: parseInt(form.id_usuario), status: parseInt(form.status) };
      if (editMode) await updateMedico(currentId, payload);
      else          await createMedico(payload);
      setShowModal(false);
      fetchMedicos();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────
  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto">

      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Médicos Odontología</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Gestione qué usuarios de la sucursal son médicos habilitados
          </p>
        </div>
        <button
          onClick={openCreate}
          className="w-full sm:w-auto bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-200 font-medium flex items-center justify-center gap-2"
        >
          <FiUserPlus size={18} /> Registrar Médico
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10">
            <Loader />
          </div>
        )}

        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto custom-scrollbar">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Nombre</th>
                <th className="px-4 py-3 font-semibold">Usuario</th>
                <th className="px-4 py-3 font-semibold">Correo</th>
                <th className="px-4 py-3 font-semibold">Teléfono</th>
                <th className="px-4 py-3 font-semibold text-center">Estado</th>
                <th className="px-4 py-3 font-semibold text-center sticky right-0 bg-slate-100 border-l border-slate-200 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">
                  Opciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {medicos.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-400 text-sm">
                    No hay médicos registrados
                  </td>
                </tr>
              )}
              {medicos.map((m) => (
                <tr key={m.id} className="hover:bg-indigo-50/50 transition-colors group">
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs">{m.id}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-black text-sm shrink-0">
                        {m.Usuario?.nombre?.charAt(0) || "?"}
                      </div>
                      <span className="whitespace-nowrap">
                        {m.Usuario?.nombre} {m.Usuario?.apellido}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">
                    @{m.Usuario?.username}
                  </td>
                  <td className="px-4 py-3 text-slate-500 max-w-[200px] truncate" title={m.Usuario?.correo}>
                    {m.Usuario?.correo}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                    {m.Usuario?.telefono || "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                      m.status === 1
                        ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                        : "bg-rose-50 text-rose-600 border-rose-200"
                    }`}>
                      {m.status === 1 ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center sticky right-0 bg-slate-100 group-hover:bg-slate-200 border-l border-slate-200 z-0 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.03)] transition-colors">
                    <button
                      onClick={() => openEdit(m)}
                      className="bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-50 hover:text-indigo-600 transition-colors text-xs font-medium"
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Móvil */}
        <div className="md:hidden divide-y divide-slate-100">
          {medicos.length === 0 && !loading && (
            <p className="p-6 text-center text-slate-400 text-sm">No hay médicos registrados</p>
          )}
          {medicos.map((m) => (
            <div key={m.id} className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-black shrink-0">
                {m.Usuario?.nombre?.charAt(0) || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-800 text-sm truncate">
                  {m.Usuario?.nombre} {m.Usuario?.apellido}
                </p>
                <p className="text-xs text-slate-400">@{m.Usuario?.username} · {m.Usuario?.correo}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                  m.status === 1
                    ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                    : "bg-rose-50 text-rose-600 border-rose-200"
                }`}>
                  {m.status === 1 ? "Activo" : "Inactivo"}
                </span>
                <button
                  onClick={() => openEdit(m)}
                  className="text-xs font-medium bg-white border border-slate-200 text-slate-600 px-3 py-1 rounded-lg hover:text-indigo-600 transition-colors"
                >
                  Editar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Modal Crear / Editar ─── */}
      {showModal && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-[100] p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header modal */}
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center">
                  {editMode ? <FiEdit size={20} /> : <FiUserPlus size={20} />}
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-base">
                    {editMode ? "Editar Médico" : "Registrar Médico"}
                  </h3>
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">
                    Odontología
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-5">
              {/* Usuario */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  <FiUser className="inline mr-1.5 text-indigo-500" size={15} />
                  Usuario de la sucursal
                </label>
                <ReactSelect
                  options={opcionesUsuarios}
                  value={opcionesUsuarios.find(o => o.value === form.id_usuario?.toString()) || null}
                  onChange={(val) => setForm(prev => ({ ...prev, id_usuario: val ? val.value : "" }))}
                  placeholder="Buscar usuario por nombre..."
                  isSearchable
                  isClearable
                  isDisabled={editMode}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    control: base => ({ ...base, borderRadius: "0.625rem", padding: "2px", border: "1px solid #e2e8f0" }),
                  }}
                  formatOptionLabel={(opt) => (
                    <div>
                      <p className="font-semibold text-slate-700">{opt.label}</p>
                      {opt.correo && <p className="text-xs text-slate-400">{opt.correo}</p>}
                    </div>
                  )}
                />
                {editMode && (
                  <p className="text-[10px] text-slate-400 mt-1 pl-1">
                    El usuario no se puede cambiar. Solo puede modificar el estado.
                  </p>
                )}
              </div>

              {/* Estado */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Estado</label>
                <ReactSelect
                  options={opcionesEstado}
                  value={opcionesEstado.find(o => o.value === parseInt(form.status)) || null}
                  onChange={(val) => setForm(prev => ({ ...prev, status: val ? val.value : 1 }))}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    control: base => ({ ...base, borderRadius: "0.625rem", padding: "2px", border: "1px solid #e2e8f0" }),
                  }}
                  formatOptionLabel={(opt) => (
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${opt.value === 1 ? "bg-emerald-500" : "bg-rose-500"}`} />
                      <span>{opt.label}</span>
                    </div>
                  )}
                />
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting || !form.id_usuario}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-sm transition-all disabled:opacity-50"
                >
                  {submitting
                    ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Guardando...</>
                    : editMode ? "Actualizar" : "Registrar Médico"
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicosOdontologia;
